/**
 * 🌱 My Growth Path — P1 Iter237
 * ================================
 *
 * Page post-test COACHDIA Prism pour le collaborateur :
 *   - Axes priorisés (3 max)
 *   - Objectifs 30/60/90 jours
 *   - Scripts verbaux prêts à l'emploi
 *   - 2 listes coachs distinctes :
 *       1. "Vos coachs accessibles" (tous eligible_coach_ids)
 *       2. "Recommandés pour vous" (top 3 scorés par l'IA)
 *
 * Source : GET /api/me/growth-path + GET /api/discovery/coach-recommendations?enterprise_order_id=X
 * SÉPARATION STRICTE entre coachs accessibles et coachs recommandés (verrou Iter237).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useEnterpriseContext } from '../contexts/EnterpriseContext';
import { getApiUrl } from '../config/environment';
import BackButton from '../components/BackButton';
import {
  Loader2, Sparkles, ArrowRight, Target, Calendar, MessageSquare,
  Users, Star, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const API = getApiUrl();

export default function MyGrowthPath() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { orders } = useEnterpriseContext();
  const [gp, setGp] = useState(null);
  const [gpLoading, setGpLoading] = useState(true);
  const [coachData, setCoachData] = useState(null);
  const [coachLoading, setCoachLoading] = useState(true);

  // Premier order actif du collab (1 seul Order avec COACHDIA Prism par cas d'usage habituel)
  const activeOrder = useMemo(
    () => (orders || []).find((o) => o.status === 'active') || (orders || [])[0] || null,
    [orders],
  );

  useEffect(() => {
    if (!token) return;
    (async () => {
      setGpLoading(true);
      try {
        const r = await fetch(`${API}/me/growth-path`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        setGp(d?.growth_path || null);
      } catch { setGp(null); }
      finally { setGpLoading(false); }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setCoachLoading(true);
      try {
        const url = activeOrder
          ? `${API}/discovery/coach-recommendations?enterprise_order_id=${activeOrder.order_id}`
          : `${API}/discovery/coach-recommendations`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) { setCoachData(null); return; }
        const d = await r.json();
        setCoachData(d);
      } catch { setCoachData(null); }
      finally { setCoachLoading(false); }
    })();
  }, [token, activeOrder]);

  if (gpLoading) {
    return (
      <div className="theme-light min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
      </div>
    );
  }

  if (!gp) {
    return (
      <div className="theme-light">
        <div className="max-w-3xl mx-auto px-4 pt-24 pb-16" data-testid="no-growth-path">
          <BackButton />
          <div className="mt-6 lt-card p-8 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto text-[#92710A] mb-3" />
            <h1 className="font-display text-2xl text-[#111827] mb-2">Aucun parcours disponible</h1>
            <p className="text-[14px] text-[#4B5563] mb-6">
              Votre parcours personnalisé sera généré dès que vous aurez finalisé votre test COACHDIA Prism.
            </p>
            <button onClick={() => navigate('/discovery')}
              className="px-5 py-2.5 rounded-full bg-[#111827] text-white text-sm font-semibold hover:bg-[#1F2937] inline-flex items-center gap-2">
              Démarrer mon test <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const accessible = coachData?.accessible_coaches || coachData?.coaches || [];
  const recommended = coachData?.recommended_coaches || [];
  const recommendedIds = new Set(recommended.map((c) => c.user_id));
  const isEnterpriseMode = !!activeOrder;

  return (
    <div className="theme-light" data-testid="my-growth-path-page">
      <div className="max-w-5xl mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <BackButton />

        <motion.header initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5EFE1] text-[#92710A] text-[11px] font-semibold uppercase tracking-wider mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Votre parcours personnalisé
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#111827] leading-tight">
            {gp.archetype_name ? <>Vous êtes <span className="text-[#92710A]">{gp.archetype_name}</span></> : 'Votre parcours'}
          </h1>
          <p className="text-[14px] text-[#4B5563] mt-2 max-w-2xl">
            Axes priorisés, objectifs 30/60/90 jours et scripts verbaux — construits à partir de votre profil COACHDIA Prism.
          </p>
        </motion.header>

        {/* AXES PRIORISÉS */}
        <section className="mb-10" data-testid="gp-axes">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-[#92710A]" />
            <h2 className="font-display text-xl text-[#111827]">3 axes priorisés</h2>
          </div>
          <p className="text-xs text-[#6B7280] mb-4">Vérité · conséquence · action pour transformer votre impact dans les 30 jours.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(gp.axes_priorises || []).slice(0, 3).map((axis, i) => (
              <div key={i} className="lt-card p-5 bg-gradient-to-br from-white to-[#FBF8F1] border-l-4 border-[#92710A]">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2 flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-[#92710A] text-white text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
                  Axe {i + 1}
                </div>
                <div className="text-sm text-[#111827] leading-relaxed">{axis}</div>
              </div>
            ))}
          </div>
        </section>

        {/* OBJECTIFS 30/60/90 */}
        {gp.objectifs_30_60_90 && (
          <section className="mb-10" data-testid="gp-objectives">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-[#92710A]" />
              <h2 className="font-display text-xl text-[#111827]">Votre plan 30 / 60 / 90 jours</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['j30', 'j60', 'j90'].map((k, i) => {
                const items = gp.objectifs_30_60_90?.[k] || [];
                const label = ['J+30', 'J+60', 'J+90'][i];
                return (
                  <div key={k} className="lt-card p-5">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">{label}</div>
                    {items.length ? (
                      <ul className="text-sm text-[#111827] space-y-1.5">
                        {items.slice(0, 4).map((it, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#047857] mt-0.5 shrink-0" />
                            <span>{typeof it === 'string' ? it : (it.title || it.objective || JSON.stringify(it))}</span>
                          </li>
                        ))}
                      </ul>
                    ) : <div className="text-xs text-[#6B7280] italic">À définir avec votre coach.</div>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* SCRIPTS VERBAUX */}
        {(gp.scripts_verbaux || []).length > 0 && (
          <section className="mb-10" data-testid="gp-scripts">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-[#92710A]" />
              <h2 className="font-display text-xl text-[#111827]">Scripts prêts à l'emploi</h2>
            </div>
            <div className="space-y-3">
              {gp.scripts_verbaux.slice(0, 4).map((s, i) => {
                const phrase = s?.phrase || s?.script || s?.content || (typeof s === 'string' ? s : '');
                if (!phrase) return null;
                return (
                  <div key={i} className="lt-card p-4">
                    {s.situation && <div className="text-[10px] tracking-widest uppercase text-[#6B7280] mb-1">{s.situation}</div>}
                    <div className="text-sm text-[#111827] italic">« {phrase} »</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* COACHS — SÉPARATION STRICTE ACCESSIBLES / RECOMMANDÉS */}
        <section className="mb-10" data-testid="gp-coaches-section">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[#92710A]" />
            <h2 className="font-display text-xl text-[#111827]">
              {isEnterpriseMode ? 'Vos coachs disponibles' : 'Coachs recommandés par l\'IA'}
            </h2>
          </div>
          {isEnterpriseMode && (
            <p className="text-xs text-[#6B7280] mb-4">
              {coachData?.company_name
                ? `Ces coachs sont présélectionnés dans la commande de ${coachData.company_name}.`
                : 'Ces coachs sont présélectionnés dans votre commande entreprise.'}
            </p>
          )}

          {coachLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-[#92710A]" />
            </div>
          ) : accessible.length === 0 ? (
            <div className="lt-card p-6 text-center text-sm text-[#6B7280]">
              {isEnterpriseMode ? 'Aucun coach validé sur cette commande pour le moment.' : 'Aucun coach recommandé — finalisez d\'abord votre profil.'}
            </div>
          ) : (
            <>
              {/* Bloc RECOMMANDÉS (iter premium distinct) */}
              {isEnterpriseMode && recommended.length > 0 && (
                <div className="mb-6" data-testid="gp-coaches-recommended">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-3 flex items-center gap-1.5">
                    <Star className="w-3 h-3 fill-[#92710A]" /> Recommandés pour vous par l'IA
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {recommended.map((c) => (
                      <CoachCard key={c.user_id} coach={c} recommended navigate={navigate} />
                    ))}
                  </div>
                </div>
              )}

              {/* Bloc ACCESSIBLES (tous) */}
              <div data-testid="gp-coaches-accessible">
                {isEnterpriseMode && recommended.length > 0 && (
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-3">
                    Tous les coachs accessibles
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {accessible.map((c) => (
                    <CoachCard key={c.user_id} coach={c}
                      recommended={recommendedIds.has(c.user_id)}
                      navigate={navigate} />
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function CoachCard({ coach, recommended, navigate }) {
  return (
    <div className="lt-card p-4 relative" data-testid={`coach-card-${coach.user_id}`}>
      {recommended && (
        <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-[#F3D060] text-[#2A2319] font-semibold flex items-center gap-0.5">
          <Star className="w-2.5 h-2.5 fill-[#2A2319]" /> Match IA
        </span>
      )}
      <div className="flex items-center gap-3 mb-2">
        {coach.avatar_url
          ? <img src={coach.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          : <div className="w-10 h-10 rounded-full bg-[#F5EFE1] border border-[#92710A]/30" />}
        <div>
          <div className="text-sm font-display text-[#111827]">{coach.display_name}</div>
          {coach.rating > 0 && <div className="text-[11px] text-[#6B7280]">⭐ {coach.rating.toFixed(1)} · {coach.total_sessions || 0} sess.</div>}
        </div>
      </div>
      {coach.bio_short && <div className="text-xs text-[#4B5563] line-clamp-2 mb-2">{coach.bio_short}</div>}
      <button
        onClick={() => navigate(`/coach/${coach.user_id}`)}
        className="text-xs px-3 py-1.5 rounded-full bg-[#111827] text-white hover:bg-[#1F2937] inline-flex items-center gap-1"
      >
        Voir le profil <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
